import { useState, useEffect } from 'react';
import { Button, Modal, ModalInner, Input, InputTextArea, Checkbox } from 'components';
import { PromotionItem, PromotionRequest } from './index';
import { validatePromotion } from './utils';

// API 요청을 위한 확장된 타입
interface PromotionRequestWithImage extends PromotionRequest {
  promotionImage?: File;
}

interface PromotionModalProps {
  isOpened: boolean;
  data: PromotionItem | null;
  onClose: () => void;
  onSave: (data: PromotionRequest) => void;
  loading?: boolean;
}

const PromotionModal = ({ isOpened, data, onClose, onSave, loading = false }: PromotionModalProps) => {
  const [formData, setFormData] = useState<PromotionRequest>({
    title: '',
    description: '',
    notice: '',
    isActive: true,
    startDate: '',
    endDate: '',
    bannerImageUrl: '',
    bannerImageFileId: undefined,
    buttonText: '',
    buttonLink: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    if (isOpened) {
      if (data) {
        setIsEditMode(true);
        setFormData({
          title: data.title || '',
          description: data.description || '',
          notice: data.notice || '',
          isActive: data.isActive ?? true,
          startDate: formatDateForInput(data.startDate || ''),
          endDate: formatDateForInput(data.endDate || ''),
          bannerImageUrl: data.bannerImageUrl || '',
          bannerImageFileId: data.bannerImageFileId,
          buttonText: data.buttonText || '',
          buttonLink: data.buttonLink || ''
        });
        setSelectedImage(null);
                 if (data.bannerImageUrl) {
           // Edit 모드에서 상대 경로를 전체 URL로 변환
           console.log('Edit mode - Original bannerImageUrl:', data.bannerImageUrl);
           console.log('Window location:', window.location.protocol, window.location.host);
           
                       // localhost는 HTTP로 접근 (SSL 인증서 문제 방지)
            let fullImageUrl = '';
            if (data.bannerImageUrl.startsWith('http')) {
              fullImageUrl = data.bannerImageUrl;
            } else {
                                // 환경에 따라 동적으로 URL 설정
                  const baseUrl = process.env.NODE_ENV === 'production' 
                    ? 'https://iopsspace.com' 
                    : 'http://localhost:8080';
                  fullImageUrl = `${baseUrl}${data.bannerImageUrl}`;
            }
           
           console.log('Generated fullImageUrl:', fullImageUrl);
           setImagePreview(fullImageUrl);
         } else {
           console.log('No bannerImageUrl found');
           setImagePreview('');
         }
      } else {
        setIsEditMode(false);
        setFormData({
          title: '',
          description: '',
          notice: '',
          isActive: true,
          startDate: '',
          endDate: '',
          bannerImageUrl: '',
          bannerImageFileId: undefined,
          buttonText: '',
          buttonLink: ''
        });
        setSelectedImage(null);
        setImagePreview('');
      }
      setErrors({});
    }
  }, [isOpened, data]);

  const handleInputChange = (field: keyof PromotionRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      
      // 이미지 미리보기 생성
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setImagePreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 날짜를 YYYY-MM-DD 형식으로 변환하는 함수
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    // YYYY-MM-DD HH:mm:ss 형식에서 YYYY-MM-DD만 추출
    return dateString.split(' ')[0];
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview('');
  };

  const handleSave = () => {
    const validation = validatePromotion(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // 날짜 형식 처리
    const saveData = {
      ...formData,
      startDate: formData.startDate || '',
      endDate: formData.endDate || ''
    };

    // 이미지 파일이 있으면 formData에 추가
    if (selectedImage) {
      (saveData as any).promotionImage = selectedImage;
    }

    onSave(saveData);
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      notice: '',
      isActive: true,
      startDate: '',
      endDate: '',
      bannerImageUrl: '',
      bannerImageFileId: undefined,
      buttonText: '',
      buttonLink: ''
    });
    setErrors({});
    setSelectedImage(null);
    setImagePreview('');
    onClose();
  };

  if (!isOpened) return null;

  return (
    <Modal isOpened={isOpened}>
      <ModalInner 
        className="md:w-[700px] max-h-[90vh] overflow-y-auto" 
        title={isEditMode ? 'Edit Promotion' : 'Add New Promotion'} 
        onClickClose={handleClose}
      >
        <div className="p-6 pt-0 space-y-4">
          <Input
            label="Title*"
            value={formData.title}
            setValue={(value) => handleInputChange('title', value)}
            placeholder="Enter promotion title"
            errorMessage={errors.title}
          />

          <InputTextArea
            label="Description*"
            inputClassName="h-24"
            value={formData.description}
            setValue={(value) => handleInputChange('description', value)}
            placeholder="Enter promotion description"
            errorMessage={errors.description}
          />

          <InputTextArea
            label="Notice*"
            inputClassName="h-32"
            value={formData.notice}
            setValue={(value) => handleInputChange('notice', value)}
            placeholder="Enter promotion notice details"
            errorMessage={errors.notice}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Start Date*"
                value={formData.startDate}
                setValue={(value) => handleInputChange('startDate', value)}
                placeholder="YYYY-MM-DD"
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
              )}
            </div>

            <div>
              <Input
                label="End Date*"
                value={formData.endDate}
                setValue={(value) => handleInputChange('endDate', value)}
                placeholder="YYYY-MM-DD"
              />
              {errors.endDate && (
                <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                프로모션 이미지
              </label>
              
                                             {/* 이미지 미리보기 */}
                {imagePreview && (
                  <div className="mb-4">
                     <img 
                       src={imagePreview} 
                       alt="프로모션 이미지 미리보기"
                       className="w-[768px] h-[430px] object-cover rounded-lg border"
                       onError={(e) => {
                         console.error('Image load failed:', imagePreview);
                         console.error('Error details:', e);
                         console.error('Target:', e.target);
                         console.error('Error type:', e.type);
                         console.error('Native event:', e.nativeEvent);
                       }}
                       onLoad={(e) => {
                         console.log('Image loaded successfully:', imagePreview);
                         const img = e.target as HTMLImageElement;
                         console.log('Image dimensions:', img.naturalWidth, 'x', img.naturalHeight);
                       }}
                     />
                  </div>
                )}
              
              {/* 파일 선택 */}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {selectedImage && (
                  <Button
                    text="제거"
                    onClick={handleRemoveImage}
                    type="secondary"
                    size="small"
                  />
                )}
              </div>
              
              <p className="text-xs text-gray-500 mt-1">
                이미지 파일을 선택하거나 기존 이미지 URL을 사용할 수 있습니다.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Button Text"
              value={formData.buttonText}
              setValue={(value) => handleInputChange('buttonText', value)}
              placeholder="e.g. Join Now (선택사항)"
              errorMessage={errors.buttonText}
            />

            <Input
              label="Button Link"
              value={formData.buttonLink}
              setValue={(value) => handleInputChange('buttonLink', value)}
              placeholder="e.g. /sign-up (선택사항)"
              errorMessage={errors.buttonLink}
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Checkbox
              value={formData.isActive}
              onChange={(checked) => handleInputChange('isActive', checked)}
              inputKey="isActive"
            />
            <span className="text-body2 font-regular text-text-primary">
              활성화 (체크 시 프로모션이 활성화됩니다)
            </span>
          </div>
        </div>

        <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
          <Button 
            className="h-9" 
            text="Cancel" 
            onClick={handleClose} 
            type="secondary" 
            size="large" 
            disabled={loading}
          />
          <Button 
            className="h-9" 
            text={loading ? "Saving..." : (isEditMode ? "Update" : "Create")} 
            onClick={handleSave} 
            type="blue" 
            size="large" 
            disabled={loading}
          />
        </div>
      </ModalInner>
    </Modal>
  );
};

export default PromotionModal;
